import React from 'react';
import { CalendarEvent } from '@hooks';
import { useTheme } from '@react-navigation/native';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { useLanguage } from '@context';
import { buildMultiDots } from './buildMultiDots';

const CalendarComponent = ({events, onDayChange}: {events: CalendarEvent[], onDayChange: (date: Dayjs | null) => void}) => {

  const theme = useTheme();
  const { currentLanguage } = useLanguage();
  // Month labels must follow the active language; computed per-locale (was a
  // module-level constant frozen to English at import time).
  const MONTHS = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => dayjs().locale(currentLanguage).month(i).format('MMMM')),
    [currentLanguage],
  );
  const eventsByDate = events.reduce<Record<string, CalendarEvent[]>>((acc, event) => {
      const date = dayjs(event.startDateTime).format('YYYY-MM-DD')
      acc[date] = [...(acc[date] || []), event];
      return acc;
  }, {});

  const CustomDay = (props: PickersDayProps<Dayjs>) => {
    const dateStr = props.day.format('YYYY-MM-DD');
    const dayEvents = eventsByDate[dateStr];
    const dots = dayEvents ? buildMultiDots(dayEvents, theme) : [];

    return (
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <PickersDay {...props} />
        {dots.length > 0 && (
          <div style={{ display: 'flex', gap: 2, position: 'absolute', bottom: 2 }}>
            {dots.map((dot) => (
              <div
                key={dot.key}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 2.5,
                  backgroundColor: dot.color,
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Custom header: two separate dropdowns (month / year) + navigation arrows,
  // instead of the single combined "June 2026" picker.
  const CalendarHeader = (props: PickersCalendarHeaderProps) => {
    const { currentMonth, onMonthChange } = props;
    const month = currentMonth.month();
    const year = currentMonth.year();
    const years = Array.from({ length: 13 }, (_, i) => year - 6 + i);

    const selectStyle: React.CSSProperties = {
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      border: `1px solid ${(theme.colors as any).grey20 ?? '#e0e0e0'}`,
      borderRadius: 10,
      padding: '8px 30px 8px 12px',
      fontSize: 15,
      fontWeight: 600,
      color: (theme.colors as any).black ?? '#1b2a4a',
      backgroundColor: '#ffffff',
      // Custom caret so both dropdowns look consistent and intentional.
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      cursor: 'pointer',
      outline: 'none',
    };

    const arrowStyle: React.CSSProperties = {
      width: 34,
      height: 34,
      borderRadius: 17,
      border: 'none',
      backgroundColor: ((theme.colors as any).primary ?? '#007AFF') + '12',
      color: (theme.colors as any).primary ?? '#007AFF',
      fontSize: 20,
      lineHeight: '34px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 8px 4px',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            aria-label="Mois"
            value={month}
            onChange={(e) => onMonthChange(currentMonth.month(Number(e.target.value)))}
            style={selectStyle}
          >
            {MONTHS.map((name, i) => (
              <option key={name} value={i}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Année"
            value={year}
            onChange={(e) => onMonthChange(currentMonth.year(Number(e.target.value)))}
            style={selectStyle}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            aria-label="Mois précédent"
            onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
            style={arrowStyle}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Mois suivant"
            onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
            style={arrowStyle}
          >
            ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLanguage}>
      <DateCalendar
        slots={{ day: CustomDay, calendarHeader: CalendarHeader }}
        onChange={onDayChange}
        views={['day']}
      />
    </LocalizationProvider>
  )
}

export default CalendarComponent
